import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import 'chart.js/auto';
import { Line } from 'react-chartjs-2';
import PropTypes from 'prop-types';
import radqcApi from 'dataExchange/radqcApiClient';
import { useDeviceCtx } from 'context/DeviceContext';
import { isCtProperty, transformData, defaultDatasetConfig, resolveChartTimePeriod } from 'utils/chart.util';

// TODO: switch to allow multiple chart types
function Chart(props) {
  const { config } = props;
  const { trackingProp, timePeriod, datasetConfigs, filter } = config || {};
  const [rawData, setRawData] = useState([]);
  const { getAccessTokenSilently } = useAuth0();
  const { devices } = useDeviceCtx();
  const device = devices.find((d) => String(d._id) === String(config?.deviceId));

  useEffect(() => {
    const getChartData = async () => {
      let receivedEntries;

      try {
        const entryType = isCtProperty(trackingProp) ? 'qc' : 'qa';
        const { unit, value } = resolveChartTimePeriod(config?.timePeriod);
        const subtractValue = unit === 'day' ? value - 1 : value;
        const startDate = moment().subtract(subtractValue, unit).subtract(1, 'month').toISOString();
        const endDate = moment().toISOString();
        const accessToken = await getAccessTokenSilently();
        const deviceId = config?.deviceId != null ? String(config.deviceId) : '';
        if (!deviceId) {
          setRawData([]);
          return;
        }
        receivedEntries = await radqcApi(
          'get',
          `/entries/${entryType}/device/${deviceId}?start=${startDate}&end=${endDate}&order=ascending`,
          { accessToken },
        );
      } catch (err) {
        console.log('Error:', err);
      }

      if (!receivedEntries) receivedEntries = [];

      setRawData(receivedEntries);
    };

    getChartData();
  }, [config?.deviceId, config?.timePeriod, trackingProp, getAccessTokenSilently]);

  if (!datasetConfigs || !trackingProp) {
    return null;
  }

  const [dataLabels, data, axisTooltipTitles] = transformData(
    trackingProp,
    rawData,
    timePeriod,
    Object.keys(datasetConfigs),
    filter,
    {
      // Geo chart bands use each entry's phantomSize; device is only for rows missing it (legacy).
      geoPhantomSize: device?.phantomSize,
    },
  );

  const datasets = Object.keys(datasetConfigs).map((tProp) => ({
    ...defaultDatasetConfig,
    ...datasetConfigs[tProp],
    data: data[tProp],
  }));

  const chartData = {
    labels: dataLabels,
    datasets,
  };

  const options = {
    maintainAspectRatio: true,
    plugins: {
      legend: {
        align: 'start',
        position: 'right',
      },
      title: {
        display: true,
        text: Object.values(datasetConfigs)[Object.values(datasetConfigs).length - 1].label,
        font: {
          size: 12,
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (tooltipItems) => {
            const idx = tooltipItems[0]?.dataIndex;
            if (idx == null) return '';
            if (axisTooltipTitles && axisTooltipTitles[idx]) return axisTooltipTitles[idx];
            if (!isCtProperty(trackingProp) && rawData[idx]?.date) {
              return moment(rawData[idx].date).format('dddd, MMM D, YYYY');
            }
            return dataLabels[idx] || '';
          },
          label: (context) => {
            const { dataIndex, datasetIndex, dataset, parsed } = context;
            const chartDatasets = context.chart.data.datasets;
            const hasRange = chartDatasets.some(
              (s) => s.label?.includes('over') || s.label?.includes('below') || s.label?.includes('under'),
            );
            if (hasRange) {
              const overIndex = chartDatasets.findIndex((s) => s.label?.includes('over'));
              const belowIndex = chartDatasets.findIndex(
                (s) => s.label?.includes('below') || s.label?.includes('under'),
              );
              const valueIndex = chartDatasets.length - 1;
              const over = overIndex >= 0 ? chartDatasets[overIndex].data[dataIndex] : null;
              const below = belowIndex >= 0 ? chartDatasets[belowIndex].data[dataIndex] : null;
              if ((over || below) && datasetIndex === valueIndex) return null;
            }
            const y = parsed?.y;
            if (y == null && y !== 0) return `${dataset.label || ''}: —`;
            return `${dataset.label || ''}: ${+Number(y).toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      y: {
        display: true,
        ticks: {
          maxTicksLimit: 11,
        },
      },
      x: {
        ticks: {
          autoSkip: true,
          autoSkipPadding: 28,
          maxTicksLimit: 10,
          maxRotation: 45,
          minRotation: 0,
        },
      },
    },
  };

  return <Line data={chartData} options={options} />;
}

Chart.propTypes = {
  config: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default Chart;
